import axios from 'axios';

/**
 * Diagnosefunktion für das Frontend, um Base64-Daten zu testen
 * @param base64Data Base64-kodierte Daten zu testen
 * @returns true wenn die Daten gültig sind
 */
export function isValidBase64(base64Data: string): boolean {
  if (!base64Data || base64Data.trim() === '') {
    return false;
  }
  
  try {
    // Teste, ob die Daten decodiert werden können
    atob(base64Data);
    return true;
  } catch (error) {
    console.error('Invalid base64 data:', error);
    return false;
  }
}

/**
 * Konvertiert Base64-Daten zu einem Blob mit dem angegebenen MIME-Typ
 * Mit besserer Fehlerbehandlung
 */
export function base64ToBlob(base64Data: string, mimeType: string): Blob | null {
  try {
    if (!isValidBase64(base64Data)) {
      throw new Error('Invalid base64 data');
    }
    
    // Konvertiere Base64 zu Blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    
    // Verwende einen Fallback MIME-Typ, wenn keiner angegeben wurde
    const actualMimeType = mimeType || 'application/octet-stream';
    
    return new Blob([byteArray], { type: actualMimeType });
  } catch (error) {
    console.error('Error converting base64 to blob:', error);
    return null;
  }
}

/**
 * Holt MIME-Typ aus dem Dateinamen (Fallback)
 */
export function getMimeTypeFromFilename(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop();
  const mimeTypes: { [key: string]: string } = {
    'pdf': 'application/pdf',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'bmp': 'image/bmp',
    'tiff': 'image/tiff',
    'tif': 'image/tiff',
    'heic': 'image/heic',
    'heif': 'image/heif',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'txt': 'text/plain',
    'html': 'text/html',
    'xml': 'application/xml'
  };
  
  return ext ? (mimeTypes[ext || ''] || 'application/octet-stream') : 'application/octet-stream';
}

/**
 * True, wenn die Datei ein Rasterbild ist (per MIME-Typ oder Dateiendung).
 * Bilder werden gescannt (Zuschnitt + OCR → durchsuchbares PDF), PDFs nicht.
 */
export function isImageFile(file?: File | null): boolean {
  if (!file) {
    return false;
  }
  if ((file.type || '').toLowerCase().startsWith('image/')) {
    return true;
  }
  // Fallback über die Dateiendung (wiederverwendet die MIME-Zuordnung oben).
  return getMimeTypeFromFilename(file.name).startsWith('image/');
}


/**
 * Führt zusätzliche Validierungen für PDF-Daten durch
 */
export function validatePdfDataHeader(base64Data: string): boolean {
  if (!isValidBase64(base64Data)) {
    return false;
  }
  
  try {
    // Überprüfe, ob die Daten mit einem PDF-Header beginnen
    // PDF-Dateien beginnen mit "%PDF-"
    const decodedStart = atob(base64Data).slice(0, 5);
    return decodedStart === '%PDF-';
  } catch (error) {
    console.error('Error validating PDF header:', error);
    return false;
  }
}

/**
 * Führt zusätzliche Validierungen für Bilddaten durch
 */
export function validateImageDataHeader(base64Data: string, mimeType: string): boolean {
  if (!isValidBase64(base64Data)) {
    return false;
  }
  
  try {
    // Sehr einfache Prüfung basierend auf bekannten Bild-Headern
    const decodedStart = atob(base64Data).slice(0, 8);
    const bytes = [];
    for (let i = 0; i < decodedStart.length; i++) {
      bytes.push(decodedStart.charCodeAt(i));
    }
    
    // JPEG: Beginnt mit 0xFF 0xD8
    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
      return bytes[0] === 0xFF && bytes[1] === 0xD8;
    }
    
    // PNG: Beginnt mit 0x89 0x50 0x4E 0x47
    if (mimeType.includes('png')) {
      return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
    }
    
    // HEIC/HEIF: Bei HEIC-Dateien prüfen wir auf die 'ftyp' Box (sollte nach 4 Bytes kommen)
    // Da das Backend HEIC zu PNG konvertiert, sollten HEIC-Dateien als PNG validiert werden
    if (mimeType.includes('heic') || mimeType.includes('heif')) {
      // Wenn der MIME-Type HEIC ist, aber die Daten bereits zu PNG konvertiert wurden,
      // prüfen wir auf PNG-Header
      if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
        return true; // Konvertierte HEIC-Datei (jetzt PNG)
      }
      // Originale HEIC-Datei würde mit 'ftyp' beginnen (an Position 4-7)
      const ftypCheck = decodedStart.slice(4, 8);
      return ftypCheck === 'ftyp';
    }
    
    // Bei anderen Bildtypen nehmen wir an, dass die Daten gültig sind,
    // wenn die Base64-Validierung erfolgreich war
    return true;
  } catch (error) {
    console.error('Error validating image header:', error);
    return false;
  }
}

/**
 * Überprüft die Dateigröße eines Base64-Strings
 * @returns Die Größe in Bytes
 */
export function getBase64Size(base64Data: string): number {
  if (!isValidBase64(base64Data)) {
    return 0;
  }
  
  // Die Größe eines Base64-String kann ungefähr berechnet werden:
  // Decodierte Größe = (Länge des Base64-Strings ohne Padding * 3) / 4
  let padding = 0;
  if (base64Data.endsWith('==')) padding = 2;
  else if (base64Data.endsWith('=')) padding = 1;
  
  return Math.floor(((base64Data.length - padding) * 3) / 4);
}

/**
 * Konvertiert Base64-Daten zu einem lesbaren Text-String
 * @param base64Data Base64-kodierte Textdaten
 * @returns Der dekodierte Text oder null bei Fehlern
 */
export function base64ToText(base64Data: string): string | null {
  try {
    if (!isValidBase64(base64Data)) {
      throw new Error('Invalid base64 data');
    }
    
    // Dekodiere Base64 zu Text
    const decodedText = atob(base64Data);
    
    // Versuche UTF-8 Dekodierung für korrekte Zeichendarstellung
    try {
      // Konvertiere zu Uint8Array und dann zu UTF-8 String
      const bytes = new Uint8Array(decodedText.length);
      for (let i = 0; i < decodedText.length; i++) {
        bytes[i] = decodedText.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch (utfError) {
      // Fallback: Verwende den direkt dekodierten Text
      return decodedText;
    }
  } catch (error) {
    console.error('Error converting base64 to text:', error);
    return null;
  }
}
